import { useCallback, useEffect, useState } from "react";
import { fetchDayStatus, fetchHistory, fetchToday } from "../api/attendance";
import { listBranches } from "../api/directory";

// module cache so re-entering the home page renders instantly, then refreshes
const cache = { branches: [], branchId: "", today: null, history: [], day: null };

export const useAttendanceData = () => {
  const [branches, setBranches] = useState(cache.branches);
  const [branchId, setBranchId] = useState(cache.branchId);
  const [today, setToday] = useState(cache.today);
  const [history, setHistory] = useState(cache.history);
  const [day, setDay] = useState(cache.day);
  const [loading, setLoading] = useState(!cache.branches.length);

  const refresh = useCallback(async () => {
    const [t, h] = await Promise.all([fetchToday(), fetchHistory(7)]);
    cache.today = t;
    cache.history = h;
    setToday(t);
    setHistory(h);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await listBranches();
        cache.branches = list;
        setBranches(list);
        setBranchId((prev) => {
          const next = prev || list[0]?.id || "";
          cache.branchId = next;
          return next;
        });
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const select = useCallback((id) => {
    cache.branchId = id;
    setBranchId(id);
  }, []);

  const branch = branches.find((b) => b.id === branchId) || null;
  useEffect(() => {
    if (!branchId) return;
    fetchDayStatus(branchId)
      .then((d) => {
        cache.day = d;
        setDay(d);
      })
      .catch(() => setDay(null));
  }, [branchId]);

  // the record date is the BRANCH local date, so compare in the branch timezone
  const branchToday = new Date(Date.now() + (branch?.tz_offset_minutes ?? 0) * 60000).toISOString().slice(0, 10);
  const todayIsCurrent = today && today.date === branchToday;
  const onLeave = todayIsCurrent && today?.status === "leave";
  const dayClosed = todayIsCurrent && today?.status === "absent";
  const nextAction =
    dayClosed || onLeave ? null : !todayIsCurrent || !today?.check_in_at ? "in" : today?.check_out_at ? null : "out";

  return {
    branches,
    branch,
    branchId,
    setBranchId: select,
    today: todayIsCurrent ? today : null,
    history,
    loading,
    refresh,
    nextAction,
    dayClosed,
    leaveType: onLeave ? today?.leave_type : null,
    day,
  };
};

export const clearAttendanceCache = () => {
  cache.branches = [];
  cache.branchId = "";
  cache.today = null;
  cache.history = [];
  cache.day = null;
};
